import { getFrontOfHouseAction, getPreparationProgress } from "../utils/restaurantWorkflow";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { selectedCompanyHasProfile } from "../utils/activityProfiles";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FireOutlined,
  MinusSquareOutlined,
  MoreOutlined,
  PlusOutlined,
  PlusSquareOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  convertRestaurantOrderToSale,
  createRestaurantOrder,
  deleteRestaurantOrder,
  getRestaurantCatalogProducts,
  getRestaurantOrder,
  getRestaurantOrders,
  sendRestaurantOrderToKitchen,
  updateRestaurantOrder,
  updateRestaurantOrderStatus,
} from "../GestionProductions/api";
import API_CONFIG from "../config/api.js";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { playBusinessAudioAlert } from "../utils/restaurantAudio";
import BusinessAudioToggle from "../components/BusinessAudioToggle";
import RestaurantOrderTicket from "./RestaurantOrderTicket";
import TicketCaisse from "../gestion-pos/TicketCaisse";
import "../gestion-produits/styles.css";
import "./RestaurantOrders.css";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const SERVICE_MODE_OPTIONS = [
  { value: "dine_in", label: "Sur place" },
  { value: "takeaway", label: "A emporter" },
  { value: "delivery", label: "Livraison" },
];

const FLOW_STATUS_META = {
  draft: { color: "default", label: "Brouillon" },
  confirmed: { color: "blue", label: "Confirmée" },
  sent_to_kitchen: { color: "gold", label: "Envoyée en cuisine" },
  in_preparation: { color: "orange", label: "En préparation" },
  ready: { color: "geekblue", label: "Prête à servir" },
  served: { color: "purple", label: "Servi" },
  closed: { color: "green", label: "Clôturée" },
  cancelled: { color: "red", label: "Annulée" },
};

const KITCHEN_STATUS_META = {
  pending: { color: "default", label: "En attente" },
  ready: { color: "blue", label: "À préparer" },
  ready_to_serve: { color: "green", label: "Prête à servir" },
  partial: { color: "orange", label: "Partielle" },
  blocked: { color: "red", label: "Bloquée" },
  not_required: { color: "default", label: "Hors production" },
  converted: { color: "green", label: "Produite" },
  cancelled: { color: "red", label: "Annulée" },
};

const FLOW_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "Tous les statuts" },
  { value: "draft", label: "Brouillons" },
  { value: "confirmed", label: "Confirmées" },
  { value: "sent_to_kitchen", label: "Envoyées en cuisine" },
  { value: "in_preparation", label: "En préparation" },
  { value: "ready", label: "Prêtes" },
  { value: "served", label: "Servies" },
  { value: "closed", label: "Clôturées" },
  { value: "cancelled", label: "Annulées" },
];

const RESTAURANT_ORDERS_AUTO_REFRESH_INTERVAL_MS = 10000;
const PRODUCTION_CONFIRMED_FLOW_STATUSES = new Set([
  "in_preparation",
  "ready",
]);

const getServiceModeLabel = (value) =>
  SERVICE_MODE_OPTIONS.find((option) => option.value === value)?.label ||
  value ||
  "-";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("fr-FR");
};

const formatDateTimeParts = (value) => {
  if (!value) return { date: "-", time: null };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { date: String(value), time: null };
  }

  return {
    date: parsed.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    time: parsed.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const formatOrderLineCount = (value) => {
  const count = Number(value) || 0;
  return `${count} article${count === 1 ? "" : "s"}`;
};

const formatMoney = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  return numeric.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatQuantity = (value, unitShortName) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  const rendered = numeric % 1 === 0 ? numeric.toString() : numeric.toFixed(2);
  return unitShortName ? `${rendered} ${unitShortName}` : rendered;
};

const buildStatusTag = (status, metaMap) => {
  const meta = metaMap[status] || { color: "default", label: status || "-" };
  return <Tag color={meta.color}>{meta.label}</Tag>;
};

const renderOrderFlowStatus = (order) => (
  <div>
    {buildStatusTag(order.flow_status, FLOW_STATUS_META)}
    {getPreparationProgress(order) && <div><Text type="secondary">{getPreparationProgress(order)}</Text></div>}
  </div>
);

const normalizeKitchenStatusForDisplay = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  if (normalized === "ready_for_production") {
    return "ready";
  }

  return normalized || "pending";
};

const deriveKitchenStatusForOrder = (order) => {
  if (Number(order.active_items) > 0 && Number(order.ready_items) === Number(order.active_items)) return "ready_to_serve";
  let lineStatuses = Array.isArray(order?.items)
    ? order.items
        .map((item) => normalizeKitchenStatusForDisplay(item?.kitchen_ticket_status))
        .filter(Boolean)
    : [];

  if (!lineStatuses.length) {
    return normalizeKitchenStatusForDisplay(order?.kitchen_status);
  }

  if (lineStatuses.every((status) => status === "cancelled")) {
    return "cancelled";
  }

  lineStatuses = lineStatuses.filter((status) => status !== "cancelled");

  if (lineStatuses.every((status) => status === "not_required")) {
    return "not_required";
  }

  const convertedCount = lineStatuses.filter(
    (status) => status === "converted"
  ).length;
  const notRequiredCount = lineStatuses.filter(
    (status) => status === "not_required"
  ).length;
  const blockedCount = lineStatuses.filter((status) => status === "blocked")
    .length;
  const pendingCount = lineStatuses.filter((status) => status === "pending")
    .length;
  const readyCount = lineStatuses.filter((status) => status === "ready").length;

  if (
    convertedCount > 0 &&
    convertedCount + notRequiredCount === lineStatuses.length
  ) {
    return "converted";
  }

  if (convertedCount > 0) {
    return "partial";
  }

  if (blockedCount > 0 && (readyCount > 0 || pendingCount > 0)) {
    return "partial";
  }

  if (blockedCount > 0 && readyCount === 0 && pendingCount === 0) {
    return "blocked";
  }

  if (pendingCount > 0) {
    return "pending";
  }

  if (readyCount > 0) {
    return "ready";
  }

  return normalizeKitchenStatusForDisplay(order?.kitchen_status);
};

const renderKitchenStatus = (record, showLabel = false) => {
  const kitchenStatus = deriveKitchenStatusForOrder(record);
  const kitchenMeta = KITCHEN_STATUS_META[kitchenStatus] || {
    label: kitchenStatus || "-",
  };

  return (
    <div className="restaurant-orders__status-secondary">
      <span
        className={`restaurant-orders__status-dot restaurant-orders__status-dot--${kitchenStatus}`}
        aria-hidden="true"
      />
      {showLabel ? (
        <span className="restaurant-orders__status-caption">Cuisine</span>
      ) : null}
      <span className="restaurant-orders__status-secondary-value">
        {kitchenMeta.label}
      </span>
    </div>
  );
};

const renderOrderStatusOverview = (record) => {
  return (
    <div className="restaurant-orders__status-overview">
      <div className="restaurant-orders__status-primary">
        {renderOrderFlowStatus(record)}
      </div>
      <span className="restaurant-orders__status-divider" aria-hidden="true" />
      {renderKitchenStatus(record, true)}
    </div>
  );
};

const getOrderItemPreview = (order, limit = 2) => {
  const items = Array.isArray(order?.items) ? order.items : [];
  const names = items
    .map((item) => item?.product_name || item?.name || null)
    .filter(Boolean);

  return {
    items: names.slice(0, limit),
    remaining: Math.max(names.length - limit, 0),
  };
};

const createEmptyDraft = () => ({
  id: null,
  order_number: null,
  service_mode: "dine_in",
  flow_status: "draft",
  table_label: "",
  guest_count: 1,
  notes_front: "",
  notes_kitchen: "",
  items: [],
});

const normalizeDraftFromOrder = (order) => ({
  id: order?.id || null,
  order_number: order?.order_number || null,
  service_mode: order?.service_mode || "dine_in",
  flow_status: order?.flow_status || "draft",
  table_label: order?.table_label || "",
  guest_count: Number(order?.guest_count) > 0 ? Number(order.guest_count) : 1,
  notes_front: order?.notes_front || "",
  notes_kitchen: order?.notes_kitchen || "",
  items: Array.isArray(order?.items)
    ? order.items.map((item) => ({
        product_id: Number(item.product_id),
        product_name: item.product_name || `Produit ${item.product_id}`,
        unit_id: item.unit_id || null,
        unit_short_name: item.unit_short_name || null,
        quantity: Number(item.quantity) || 0,
        unit_price: Number(item.unit_price) || 0,
        course_label: item.course_label || "",
        notes: item.notes || "",
      }))
    : [],
});

const computeDraftTotals = (items = []) =>
  items.reduce(
    (acc, item) => {
      const quantity = Number(item?.quantity) || 0;
      const unitPrice = Number(item?.unit_price) || 0;
      return {
        total: acc.total + quantity * unitPrice,
        total_items: acc.total_items + 1,
        total_quantity: acc.total_quantity + quantity,
      };
    },
    { total: 0, total_items: 0, total_quantity: 0 }
  );

const buildDetailDescriptionSpan = (isMobileLayout) => (isMobileLayout ? 1 : 2);

const RestaurantOrders = () => {
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const screens = useBreakpoint();
  const isMobileLayout = !screens.md;

  const isRestaurantActivity = selectedCompanyHasProfile(
    user,
    selectedCompany,
    "restaurant"
  );
  const canView = hasPermission(
    "Gestion Commerciale.Restauration.CommandesSalle.view"
  );
  const canCreate = hasPermission(
    "Gestion Commerciale.Restauration.CommandesSalle.create"
  );
  const canEdit = hasPermission(
    "Gestion Commerciale.Restauration.CommandesSalle.edit"
  );
  const canDelete = hasPermission(
    "Gestion Commerciale.Restauration.CommandesSalle.delete"
  );
  const canSend = hasPermission(
    "Gestion Commerciale.Restauration.CommandesSalle.send_to_kitchen"
  );
  const canConvert =
    hasPermission(
      "Gestion Commerciale.Restauration.CommandesSalle.convert_to_sale"
    ) || hasPermission("Gestion Commerciale.POS.use");

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersPagination, setOrdersPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [flowStatusFilter, setFlowStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [draft, setDraft] = useState(createEmptyDraft());
  const [detailOrder, setDetailOrder] = useState(null);
  const [ticketOrder, setTicketOrder] = useState(null);
  const [printingOrderId, setPrintingOrderId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Encaissement / conversion en vente
  const [payOrder, setPayOrder] = useState(null);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const [paymentModesLoading, setPaymentModesLoading] = useState(false);
  const [payModeId, setPayModeId] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [cashTicket, setCashTicket] = useState(null);

  const [productCatalog, setProductCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const hasNotificationBaselineRef = useRef(false);
  const previousOrderFlowStatusesRef = useRef(new Map());

  const loadOrders = useCallback(async ({ silent = false } = {}) => {
    if (!selectedCompany || !selectedWarehouse || !isRestaurantActivity) {
      setOrders([]);
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    try {
      const params = {
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
        page: 1,
        limit: 100,
      };
      if (flowStatusFilter && flowStatusFilter !== "all") {
        params.flow_status = flowStatusFilter;
      }
      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      const data = await getRestaurantOrders(params);
      const nextOrders = Array.isArray(data?.orders) ? data.orders : [];
      const nextStatusMap = new Map(
        nextOrders
          .map((order) => {
            const orderId = Number(order?.id);
            if (!Number.isFinite(orderId) || orderId <= 0) {
              return null;
            }

            return [
              orderId,
              String(order?.flow_status || "")
                .trim()
                .toLowerCase(),
            ];
          })
          .filter(Boolean)
      );

      if (hasNotificationBaselineRef.current) {
        const hasProductionConfirmation = Array.from(nextStatusMap.entries()).some(
          ([orderId, nextFlowStatus]) => {
            const previousFlowStatus =
              previousOrderFlowStatusesRef.current.get(orderId) || "";

            return (
              previousFlowStatus &&
              !PRODUCTION_CONFIRMED_FLOW_STATUSES.has(previousFlowStatus) &&
              PRODUCTION_CONFIRMED_FLOW_STATUSES.has(nextFlowStatus)
            );
          }
        );

        if (hasProductionConfirmation) {
          void playBusinessAudioAlert("production_confirmed");
        }
      } else {
        hasNotificationBaselineRef.current = true;
      }

      previousOrderFlowStatusesRef.current = nextStatusMap;
      setOrders(nextOrders);
    } catch (error) {
      console.error("Erreur lors du chargement des commandes restaurant:", error);
      if (!silent) {
        message.error(
          error?.error ||
            error?.message ||
            "Erreur lors du chargement des commandes restaurant"
        );
        setOrders([]);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [
    flowStatusFilter,
    isRestaurantActivity,
    searchText,
    selectedCompany,
    selectedWarehouse,
  ]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    hasNotificationBaselineRef.current = false;
    previousOrderFlowStatusesRef.current = new Map();
    setOrdersPagination((previous) => ({ ...previous, current: 1 }));
  }, [selectedCompany, selectedWarehouse, flowStatusFilter, searchText]);

  useEffect(() => {
    if (!selectedCompany || !selectedWarehouse || !isRestaurantActivity) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadOrders({ silent: true });
    }, RESTAURANT_ORDERS_AUTO_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isRestaurantActivity, loadOrders, selectedCompany, selectedWarehouse]);

  const loadCatalog = useCallback(
    async (searchValue = "") => {
      if (!selectedCompany || !selectedWarehouse) {
        setProductCatalog([]);
        return;
      }

      setCatalogLoading(true);
      try {
        const data = await getRestaurantCatalogProducts({
          company_id: selectedCompany,
          warehouse_id: selectedWarehouse,
          search: searchValue || undefined,
        });
        const products = Array.isArray(data?.products)
          ? data.products
          : Array.isArray(data)
          ? data
          : [];
        setProductCatalog(products);
      } catch (error) {
        console.error("Erreur catalogue produits restaurant:", error);
        message.error(
          error?.error || error?.message || "Erreur lors du chargement du catalogue"
        );
        setProductCatalog([]);
      } finally {
        setCatalogLoading(false);
      }
    },
    [selectedCompany, selectedWarehouse]
  );

  const openCreateDrawer = () => {
    setDraft(createEmptyDraft());
    setDrawerMode("create");
    setDrawerOpen(true);
    loadCatalog("");
  };

  const openEditDrawer = (order) => {
    setDraft(normalizeDraftFromOrder(order));
    setDrawerMode("edit");
    setDrawerOpen(true);
    loadCatalog("");
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDraft(createEmptyDraft());
    setCatalogSearch("");
  };

  const openDetailModal = (order) => {
    setDetailOrder(order || null);
  };

  const closeDetailModal = () => {
    setDetailOrder(null);
  };

  const openPrintTicket = useCallback(
    async (order) => {
      if (!order) {
        return;
      }

      const orderId = Number(order.id);
      if (!Number.isFinite(orderId) || orderId <= 0) {
        setTicketOrder(order);
        return;
      }

      setPrintingOrderId(orderId);
      try {
        const latestOrder = await getRestaurantOrder(orderId, {
          company_id: selectedCompany,
        });
        setTicketOrder({
          ...order,
          ...latestOrder,
          items: Array.isArray(latestOrder?.items) ? latestOrder.items : order.items,
        });
      } catch (error) {
        console.error("Erreur chargement ticket commande restaurant:", error);
        message.warning(
          "Impossible de recharger le detail. Le ticket utilise les donnees visibles."
        );
        setTicketOrder(order);
      } finally {
        setPrintingOrderId(null);
      }
    },
    [selectedCompany]
  );

  const closePrintTicket = () => {
    setTicketOrder(null);
  };

  const loadPaymentModes = useCallback(async () => {
    if (!selectedCompany) {
      setPaymentModes([]);
      return [];
    }
    setPaymentModesLoading(true);
    try {
      const response = await axios.get(API_CONFIG.apiUrl("/payment-modes"), {
        params: { page: 1, limit: 1000, company_id: selectedCompany },
      });
      const data = response?.data || {};
      const modes = Array.isArray(data)
        ? data
        : data.paymentModes || data.payment_modes || [];
      setPaymentModes(modes);
      return modes;
    } catch (error) {
      console.error("Erreur chargement modes de paiement:", error);
      setPaymentModes([]);
      return [];
    } finally {
      setPaymentModesLoading(false);
    }
  }, [selectedCompany]);

  const openPaymentModal = useCallback(
    (record) => {
      if (!record) return;
      setPayOrder(record);
      setPayAmount(Number(record.total) || 0);
      setPayModeId(null);
      loadPaymentModes().then((modes) => {
        if (Array.isArray(modes) && modes.length === 1) {
          setPayModeId(modes[0].id);
        }
      });
    },
    [loadPaymentModes]
  );

  const closePaymentModal = () => {
    if (paySubmitting) return;
    setPayOrder(null);
    setPayModeId(null);
    setPayAmount(0);
  };

  const closeCashTicket = () => {
    setCashTicket(null);
  };

  const handleConfirmPayment = async () => {
    if (!payOrder) return;
    const amount = Number(payAmount) || 0;
    if (amount > 0 && !payModeId) {
      message.warning("Veuillez selectionner un mode de paiement.");
      return;
    }

    const orderTotal = Number(payOrder.total) || 0;
    const isPartial = amount > 0 && amount < orderTotal - 0.0001;

    const submit = async () => {
      setPaySubmitting(true);
      try {
        const response = await convertRestaurantOrderToSale(payOrder.id, {
          company_id: selectedCompany,
          warehouse_id: payOrder.warehouse_id || selectedWarehouse,
          payment:
            amount > 0
              ? {
                  amount,
                  payment_mode_id: payModeId,
                  date: new Date().toISOString().split("T")[0],
                }
              : { amount: 0 },
        });

        const modeName =
          paymentModes.find((mode) => mode.id === payModeId)?.name || "Especes";
        const ticketData = {
          order: {
            id: response.orderId,
            invoice_number: response.invoice_number,
            source_order_number: response.source_order_number,
            order_date: new Date().toISOString(),
            total: response.total ?? response.order?.total ?? orderTotal,
            subtotal: response.subtotal ?? 0,
            tax_amount: response.tax_amount ?? 0,
            discount: 0,
            paid_amount: response.order?.paid_amount ?? amount,
            due_amount: response.order?.due_amount ?? Math.max(orderTotal - amount, 0),
            payment_mode: modeName,
            payment_status: response.order?.payment_status || "",
            order_status: response.order?.order_status || "delivered",
            warehouse_id: response.warehouse_id || payOrder.warehouse_id,
            client_name: response.client_name || "Client de passage",
          },
          items: Array.isArray(response.ticket_items) ? response.ticket_items : [],
          customer: { name: response.client_name || "Client de passage" },
          staff_member: { name: user?.name || "Vendeur" },
          server: null,
        };

        message.success("Commande encaissee et convertie en vente.");
        setPayOrder(null);
        setPayModeId(null);
        setPayAmount(0);
        setCashTicket(ticketData);
        loadOrders({ silent: true });
      } catch (error) {
        const errMessage =
          error?.error || error?.message || "Echec de l'encaissement.";
        message.error(errMessage);
      } finally {
        setPaySubmitting(false);
      }
    };

    if (isPartial) {
      Modal.confirm({
        title: "Encaissement partiel",
        content: `Le montant saisi (${formatMoney(amount)} CFA) est inferieur au total de la commande (${formatMoney(
          orderTotal
        )} CFA). Confirmer l'encaissement partiel ?`,
        okText: "Confirmer",
        cancelText: "Annuler",
        onOk: submit,
      });
      return;
    }

    await submit();
  };

  const updateDraftField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const addProductToDraft = (product) => {
    if (!product) return;
    setDraft((prev) => {
      const existing = prev.items.find(
        (item) => Number(item.product_id) === Number(product.id)
      );
      if (existing) {
        return {
          ...prev,
          items: prev.items.map((item) =>
            Number(item.product_id) === Number(product.id)
              ? { ...item, quantity: Number(item.quantity) + 1 }
              : item
          ),
        };
      }
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            product_id: Number(product.id),
            product_name: product.name,
            unit_id: product.unit_id || null,
            unit_short_name: product.unit_short_name || null,
            quantity: 1,
            unit_price: Number(product.sales_price) || 0,
            course_label: "",
            notes: "",
          },
        ],
      };
    });
  };

  const updateDraftItem = (index, field, value) => {
    setDraft((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeDraftItem = (index) => {
    setDraft((prev) => ({
      ...prev,
      items: prev.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const buildPayload = (nextFlowStatus = null) => {
    const items = draft.items.map((item) => ({
      product_id: Number(item.product_id),
      quantity: Number(item.quantity) || 0,
      unit_id: item.unit_id || undefined,
      unit_price: Number(item.unit_price) || 0,
      course_label: item.course_label || undefined,
      notes: item.notes || undefined,
    }));

    return {
      company_id: selectedCompany,
      warehouse_id: selectedWarehouse,
      service_mode: draft.service_mode,
      flow_status: nextFlowStatus || draft.flow_status || "draft",
      table_label: draft.table_label || null,
      guest_count: Number(draft.guest_count) || 1,
      notes_front: draft.notes_front || null,
      notes_kitchen: draft.notes_kitchen || null,
      items,
    };
  };

  const validateDraft = (payload) => {
    if (!payload.items.length) {
      message.warning("Ajoutez au moins un article a la commande.");
      return false;
    }
    if (payload.service_mode === "dine_in" && !payload.table_label) {
      message.warning("Le numero ou nom de table est requis pour une commande sur place.");
      return false;
    }
    const hasInvalidQty = payload.items.some(
      (item) => !Number.isFinite(item.quantity) || item.quantity <= 0
    );
    if (hasInvalidQty) {
      message.warning("Chaque article doit avoir une quantite strictement positive.");
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    const payload = buildPayload();
    if (!validateDraft(payload)) return;

    setSaving(true);
    try {
      if (drawerMode === "edit" && draft.id) {
        await updateRestaurantOrder(draft.id, payload);
        message.success("Commande restaurant mise a jour.");
      } else {
        await createRestaurantOrder(payload);
        message.success("Commande restaurant creee.");
      }
      closeDrawer();
      loadOrders();
    } catch (error) {
      console.error("Erreur enregistrement commande restaurant:", error);
      message.error(
        error?.error || error?.message || "Erreur lors de l'enregistrement"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    if (!canSend) {
      message.warning("Vous n'avez pas la permission d'envoyer en cuisine.");
      return;
    }

    const payload = buildPayload("confirmed");
    if (!validateDraft(payload)) return;

    setSaving(true);
    try {
      let orderId = draft.id;
      if (drawerMode === "edit" && orderId) {
        await updateRestaurantOrder(orderId, payload);
      } else {
        const result = await createRestaurantOrder(payload);
        orderId = result?.order_id;
      }

      if (!orderId) {
        throw new Error("Identifiant de commande indisponible apres sauvegarde.");
      }

      const sendResult = await sendRestaurantOrderToKitchen(orderId, {
        company_id: selectedCompany,
      });

      const summary = sendResult?.summary || {};
      const summaryParts = [];
      if (summary.ready_for_production) {
        summaryParts.push(`${summary.ready_for_production} prets`);
      }
      if (summary.blocked) {
        summaryParts.push(`${summary.blocked} bloques`);
      }
      if (summary.not_required) {
        summaryParts.push(`${summary.not_required} hors production`);
      }
      message.success(
        summaryParts.length
          ? `Commande envoyee en cuisine (${summaryParts.join(", ")}).`
          : "Commande envoyee en cuisine."
      );

      closeDrawer();
      loadOrders();
    } catch (error) {
      console.error("Erreur envoi commande restaurant en cuisine:", error);
      message.error(
        error?.error || error?.message || "Erreur lors de l'envoi en cuisine"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrder = async (order) => {
    try {
      await deleteRestaurantOrder(order.id, {
        company_id: selectedCompany,
      });
      message.success("Commande restaurant supprimee.");
      loadOrders();
    } catch (error) {
      console.error("Erreur suppression commande restaurant:", error);
      message.error(
        error?.error || error?.message || "Erreur lors de la suppression"
      );
    }
  };

  const confirmDeleteOrder = (order) => {
    Modal.confirm({
      title: "Supprimer cette commande ?",
      content: `La commande ${order.order_number || `#${order.id}`} sera supprimée définitivement.`,
      okText: "Oui, supprimer",
      okButtonProps: { danger: true },
      cancelText: "Non",
      onOk: () => handleDeleteOrder(order),
    });
  };

  const handleServiceAction = async (order) => {
    const nextStatus = getFrontOfHouseAction(order.flow_status);
    if (!canEdit || !["served", "closed"].includes(nextStatus)) return;
    try {
      await updateRestaurantOrderStatus(order.id, {
        company_id: selectedCompany,
        flow_status: nextStatus,
      });
      message.success(nextStatus === "served" ? "Commande marquée servie." : "Commande clôturée.");
      loadOrders();
    } catch (error) {
      message.error(error?.details || error?.error || error?.message || "Action impossible.");
    }
  };

  const handleCancelOrder = (order) => {
    Modal.confirm({
      title: "Annuler cette commande ?",
      content: `La commande ${order.order_number} sera marquee comme annulee.`,
      okText: "Oui, annuler",
      okButtonProps: { danger: true },
      cancelText: "Non",
      onOk: async () => {
        try {
          await updateRestaurantOrderStatus(order.id, {
            company_id: selectedCompany,
            flow_status: "cancelled",
          });
          message.success("Commande annulee.");
          loadOrders();
        } catch (error) {
          console.error("Erreur annulation commande restaurant:", error);
          message.error(
            error?.details || error?.error || error?.message || "Erreur lors de l'annulation"
          );
        }
      },
    });
  };

  const handleSendExistingToKitchen = async (order) => {
    try {
      const result = await sendRestaurantOrderToKitchen(order.id, {
        company_id: selectedCompany,
      });
      const summary = result?.summary || {};
      const summaryParts = [];
      if (summary.ready_for_production) {
        summaryParts.push(`${summary.ready_for_production} prets`);
      }
      if (summary.blocked) {
        summaryParts.push(`${summary.blocked} bloques`);
      }
      if (summary.not_required) {
        summaryParts.push(`${summary.not_required} hors production`);
      }
      message.success(
        summaryParts.length
          ? `Commande envoyee en cuisine (${summaryParts.join(", ")}).`
          : "Commande envoyee en cuisine."
      );
      loadOrders();
    } catch (error) {
      console.error("Erreur envoi commande restaurant en cuisine:", error);
      message.error(
        error?.error || error?.message || "Erreur lors de l'envoi en cuisine"
      );
    }
  };

  const filteredCatalog = useMemo(() => {
    if (!catalogSearch.trim()) return productCatalog;
    const needle = catalogSearch.trim().toLowerCase();
    return productCatalog.filter((product) =>
      String(product.name || "").toLowerCase().includes(needle)
    );
  }, [catalogSearch, productCatalog]);

  const draftTotals = useMemo(() => computeDraftTotals(draft.items), [draft.items]);
  const isEditable =
    drawerMode === "create" ||
    ["draft", "confirmed"].includes(draft.flow_status);
  const drawerWidth = isMobileLayout ? "100%" : 960;
  const detailOrderItems = Array.isArray(detailOrder?.items) ? detailOrder.items : [];
  const detailOrderTitle = detailOrder
    ? `Details commande ${detailOrder.order_number || `#${detailOrder.id}`}`
    : "Details commande";

  const renderOrderActions = (record) => {
    const serviceAction = getFrontOfHouseAction(record.flow_status);
    const canProgressService = canEdit && ["served", "closed"].includes(serviceAction);
    const serviceLabel = serviceAction === "closed" ? "Clôturer" : "Marquer servie";
    const editable = ["draft", "confirmed"].includes(record.flow_status);
    const sendable = canSend && editable;
    const deletable = canDelete && editable;
    const cancellable =
      canEdit && ["draft", "confirmed", "sent_to_kitchen", "in_preparation"].includes(record.flow_status) && !record.source_sales_order_id;
    const alreadyConverted = Boolean(record.source_sales_order_id);
    const payable =
      canConvert &&
      !alreadyConverted &&
      record.flow_status !== "cancelled" &&
      Number(record.total) > 0;

    if (!isMobileLayout) {
      const isPrinting = printingOrderId === Number(record.id);
      const actionItems = [
        {
          key: "view",
          icon: <EyeOutlined />,
          label: "Voir le détail",
          onClick: () => openDetailModal(record),
        },
        {
          key: "print",
          icon: <PrinterOutlined />,
          label: isPrinting ? "Impression en cours…" : "Imprimer le ticket",
          disabled: isPrinting,
          onClick: () => openPrintTicket(record),
        },
        {
          key: "edit",
          icon: <EditOutlined />,
          label: editable ? "Modifier" : "Commande non modifiable",
          disabled: !canEdit || !editable,
          onClick: () => openEditDrawer(record),
        },
        { type: "divider" },
        {
          key: "kitchen",
          icon: <SendOutlined />,
          label: sendable ? "Envoyer en cuisine" : "Envoi en cuisine indisponible",
          disabled: !sendable,
          onClick: () => handleSendExistingToKitchen(record),
        },
        {
          key: "service",
          icon: <CheckCircleOutlined />,
          label: serviceLabel,
          disabled: !canProgressService,
          onClick: () => handleServiceAction(record),
        },
        {
          key: "payment",
          icon: <DollarOutlined />,
          label: alreadyConverted
            ? "Commande déjà encaissée"
            : payable
            ? "Encaisser la commande"
            : "Encaissement indisponible",
          disabled: !payable,
          onClick: () => openPaymentModal(record),
        },
        { type: "divider" },
        {
          key: "cancel",
          icon: <StopOutlined />,
          label: cancellable ? "Annuler la commande" : "Annulation indisponible",
          disabled: !cancellable,
          danger: cancellable,
          onClick: () => handleCancelOrder(record),
        },
        {
          key: "delete",
          icon: <DeleteOutlined />,
          label: deletable ? "Supprimer définitivement" : "Suppression indisponible",
          disabled: !deletable,
          danger: deletable,
          onClick: () => confirmDeleteOrder(record),
        },
      ];

      return (
        <Dropdown
          menu={{ items: actionItems }}
          trigger={["hover", "click"]}
          placement="bottomRight"
          overlayClassName="restaurant-orders__actions-dropdown"
          mouseEnterDelay={0.12}
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            loading={isPrinting}
            className="restaurant-orders__more-action"
            aria-label={`Afficher les actions de la commande ${
              record.order_number || record.id
            }`}
            title="Actions"
          />
        </Dropdown>
      );
    }

    return (
      <div className="restaurant-orders__mobile-actions">
        <Button
          size="middle"
          icon={<PrinterOutlined />}
          onClick={() => openPrintTicket(record)}
          loading={printingOrderId === Number(record.id)}
          className="restaurant-orders__mobile-action"
        >
          Ticket
        </Button>
        <Button
          size="middle"
          icon={<EditOutlined />}
          onClick={() => openEditDrawer(record)}
          disabled={!canEdit || !editable}
          className="restaurant-orders__mobile-action"
        >
          Modifier
        </Button>
        <Button
          size="middle"
          type="primary"
          icon={<SendOutlined />}
          onClick={() => handleSendExistingToKitchen(record)}
          disabled={!sendable}
          className="restaurant-orders__mobile-action restaurant-orders__mobile-action--primary"
        >
          Cuisine
        </Button>
        <Button
          size="middle"
          icon={<CheckCircleOutlined />}
          onClick={() => handleServiceAction(record)}
          disabled={!canProgressService}
          className="restaurant-orders__mobile-action"
        >
          {serviceLabel}
        </Button>
        <Button
          size="middle"
          icon={<DollarOutlined />}
          onClick={() => openPaymentModal(record)}
          disabled={!payable}
          className="restaurant-orders__mobile-action"
          style={
            payable ? { color: "#52c41a", borderColor: "#52c41a" } : undefined
          }
        >
          Encaisser
        </Button>
        <Button
          size="middle"
          icon={<StopOutlined />}
          onClick={() => handleCancelOrder(record)}
          disabled={!cancellable}
          className="restaurant-orders__mobile-action restaurant-orders__mobile-action--cancel"
        >
          Annuler
        </Button>
        <Popconfirm
          title="Supprimer cette commande ?"
          okText="Oui"
          cancelText="Non"
          onConfirm={() => handleDeleteOrder(record)}
          disabled={!deletable}
        >
          <Button
            size="middle"
            danger
            icon={<DeleteOutlined />}
            disabled={!deletable}
            className="restaurant-orders__mobile-action restaurant-orders__mobile-action--danger"
          >
            Supprimer
          </Button>
        </Popconfirm>
      </div>
    );
  };

  const draftItemColumns = [
    {
      title: "Article",
      dataIndex: "product_name",
      key: "product_name",
      render: (value, record) => (
        <div>
          <Text strong>{value}</Text>
          {record.unit_short_name && (
            <div>
              <Text type="secondary">{record.unit_short_name}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Qte",
      key: "quantity",
      width: 110,
      render: (_, record, index) => (
        <InputNumber
          min={0.01}
          step={1}
          value={record.quantity}
          onChange={(value) => updateDraftItem(index, "quantity", value || 0)}
          disabled={!isEditable}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "PU",
      key: "unit_price",
      width: 130,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          step={100}
          value={record.unit_price}
          onChange={(value) => updateDraftItem(index, "unit_price", value || 0)}
          disabled={!isEditable}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Sous-total",
      key: "subtotal",
      width: 110,
      align: "right",
      render: (_, record) => (
        <Text>
          {formatMoney(
            (Number(record.quantity) || 0) * (Number(record.unit_price) || 0)
          )}
        </Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 40,
      render: (_, __, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeDraftItem(index)}
          disabled={!isEditable}
        />
      ),
    },
  ];

  const orderItemColumns = [
    {
      title: "Article",
      dataIndex: "product_name",
      key: "product_name",
      render: (value, item) => (
        <div className="restaurant-orders__expanded-product">
          <Text strong>{value || `Produit ${item.product_id}`}</Text>
          {item.course_label ? (
            <Text type="secondary">{item.course_label}</Text>
          ) : null}
        </div>
      ),
    },
    {
      title: "Quantite",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (value, item) => formatQuantity(value, item?.unit_short_name),
    },
    {
      title: "Prix unitaire",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 130,
      align: "right",
      render: (value) => `${formatMoney(value)} CFA`,
    },
    {
      title: "Sous-total",
      key: "subtotal",
      width: 140,
      align: "right",
      render: (_, item) =>
        `${formatMoney(
          (Number(item?.quantity) || 0) * (Number(item?.unit_price) || 0)
        )} CFA`,
    },
    {
      title: "Note",
      dataIndex: "notes",
      key: "notes",
      render: (value) => value || "-",
    },
  ];

  const renderDetailOrderContent = () => {
    if (!detailOrder) {
      return null;
    }

    return (
      <div className="restaurant-orders__details-modal">
        <Descriptions
          bordered
          size="small"
          column={isMobileLayout ? 1 : 2}
          layout={isMobileLayout ? "vertical" : "horizontal"}
          className="restaurant-orders__details-descriptions"
        >
          <Descriptions.Item label="Date">
            {formatDateTime(detailOrder.order_date)}
          </Descriptions.Item>
          <Descriptions.Item label="Commande">
            {detailOrder.order_number || `#${detailOrder.id}`}
          </Descriptions.Item>
          <Descriptions.Item label="Table">
            {detailOrder.table_label || "Sans table"}
          </Descriptions.Item>
          <Descriptions.Item label="Service">
            {getServiceModeLabel(detailOrder.service_mode)}
          </Descriptions.Item>
          <Descriptions.Item label="Couvert">
            {Number(detailOrder.guest_count) || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Serveur">
            {detailOrder.waiter_name || "Non renseigne"}
          </Descriptions.Item>
          <Descriptions.Item label="Suivi salle">
            {buildStatusTag(detailOrder.flow_status, FLOW_STATUS_META)}
          </Descriptions.Item>
          <Descriptions.Item label="Suivi cuisine">
            {buildStatusTag(
              deriveKitchenStatusForOrder(detailOrder),
              KITCHEN_STATUS_META
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Montant">
            <Text strong>{formatMoney(detailOrder.total)} CFA</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Quantite totale">
            {formatQuantity(detailOrder.total_quantity)}
          </Descriptions.Item>
          <Descriptions.Item
            label="Note salle"
            span={buildDetailDescriptionSpan(isMobileLayout)}
          >
            {detailOrder.notes_front || "Aucune note salle."}
          </Descriptions.Item>
          <Descriptions.Item
            label="Note cuisine"
            span={buildDetailDescriptionSpan(isMobileLayout)}
          >
            {detailOrder.notes_kitchen || "Aucune note cuisine."}
          </Descriptions.Item>
        </Descriptions>

        <div className="restaurant-orders__details-items">
          <div className="restaurant-orders__details-items-head">
            <strong>Articles de la commande</strong>
            <span>
              {Number(detailOrder.total_items) || 0} ligne(s) -{" "}
              {formatQuantity(detailOrder.total_quantity)}
            </span>
          </div>

          {isMobileLayout ? (
            detailOrderItems.length === 0 ? (
              <Empty description="Aucun article sur cette commande." />
            ) : (
              <div className="restaurant-orders__details-item-cards">
                {detailOrderItems.map((item, index) => (
                  <article
                    key={`${detailOrder.id}-${item.product_id || "item"}-${index}`}
                    className="restaurant-orders__details-item-card"
                  >
                    <div className="restaurant-orders__details-item-head">
                      <div className="restaurant-orders__details-item-title-group">
                        <strong>{item.product_name || `Produit ${item.product_id}`}</strong>
                        {item.course_label ? <span>{item.course_label}</span> : null}
                      </div>
                      <span className="restaurant-orders__details-item-total">
                        {formatMoney(
                          (Number(item?.quantity) || 0) *
                            (Number(item?.unit_price) || 0)
                        )}{" "}
                        CFA
                      </span>
                    </div>

                    <div className="restaurant-orders__details-item-grid">
                      <div className="restaurant-orders__details-item-metric">
                        <span className="restaurant-orders__details-item-label">
                          Quantite
                        </span>
                        <strong>
                          {formatQuantity(item.quantity, item?.unit_short_name)}
                        </strong>
                      </div>
                      <div className="restaurant-orders__details-item-metric">
                        <span className="restaurant-orders__details-item-label">
                          Prix unitaire
                        </span>
                        <strong>{formatMoney(item.unit_price)} CFA</strong>
                      </div>
                    </div>

                    {item.notes ? (
                      <div className="restaurant-orders__details-item-note">
                        Note : {item.notes}
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            )
          ) : (
            <Table
              rowKey={(item, index) =>
                `${detailOrder.id}-${item.product_id || "item"}-${index}`
              }
              size="small"
              pagination={false}
              columns={orderItemColumns}
              dataSource={detailOrderItems}
              locale={{
                emptyText: "Aucun article sur cette commande.",
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const desktopExpandable = {
    expandedRowRender: (record) => (
      <div className="restaurant-orders__expanded-panel">
        <Table
          rowKey={(item, index) =>
            `${record.id}-${item.product_id || "item"}-${index}`
          }
          size="small"
          pagination={false}
          columns={orderItemColumns}
          dataSource={Array.isArray(record?.items) ? record.items : []}
          locale={{
            emptyText: "Aucun article sur cette commande.",
          }}
        />
      </div>
    ),
    rowExpandable: (record) =>
      Array.isArray(record?.items) && record.items.length > 0,
    expandIcon: ({ expanded, onExpand, record }) =>
      Array.isArray(record?.items) && record.items.length > 0 ? (
        <Button
          type="text"
          size="small"
          icon={expanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
          onClick={(event) => onExpand(record, event)}
          className="restaurant-orders__expand-icon"
          aria-label={expanded ? "Replier" : "Deplier"}
        />
      ) : (
        <span className="restaurant-orders__expand-placeholder" />
      ),
  };

  const showSeparateOperationalColumns = Boolean(screens.xxl);
  const dateTimeColumns = showSeparateOperationalColumns
    ? [
        {
          title: "Date",
          dataIndex: "order_date",
          key: "order_date",
          width: 112,
          render: (value) => (
            <span className="restaurant-orders__date-day">
              {formatDateTimeParts(value).date}
            </span>
          ),
        },
        {
          title: "Heure",
          dataIndex: "order_date",
          key: "order_time",
          width: 82,
          render: (value) => (
            <span className="restaurant-orders__time-value">
              {formatDateTimeParts(value).time || "-"}
            </span>
          ),
        },
      ]
    : [
        {
          title: "Date / heure",
          dataIndex: "order_date",
          key: "order_date",
          width: 172,
          render: (value) => {
            const dateTime = formatDateTimeParts(value);

            return (
              <div className="restaurant-orders__date-cell">
                <span className="restaurant-orders__date-day">
                  {dateTime.date}
                </span>
                {dateTime.time ? (
                  <>
                    <span
                      className="restaurant-orders__date-separator"
                      aria-hidden="true"
                    >
                      ·
                    </span>
                    <span className="restaurant-orders__date-time">
                      {dateTime.time}
                    </span>
                  </>
                ) : null}
              </div>
            );
          },
        },
      ];

  const statusColumns = showSeparateOperationalColumns
    ? [
        {
          title: "Statut",
          key: "flow_status",
          width: 145,
          render: (_, record) => (
            <div className="restaurant-orders__status-primary">
              {renderOrderFlowStatus(record)}
            </div>
          ),
        },
        {
          title: "Cuisine",
          key: "kitchen_status",
          width: 170,
          render: (_, record) => renderKitchenStatus(record),
        },
      ]
    : [
        {
          title: "Suivi",
          key: "flow_status",
          width: 285,
          render: (_, record) => renderOrderStatusOverview(record),
        },
      ];

  const columns = [
    ...dateTimeColumns,
    {
      title: "Commande",
      key: "order",
      width: 190,
      render: (_, record) => (
        <div className="restaurant-orders__cell-stack">
          <Text strong className="restaurant-orders__cell-title">
            {record.order_number || `#${record.id}`}
          </Text>
          <span className="restaurant-orders__order-meta">
            {formatOrderLineCount(record.total_items)}
          </span>
        </div>
      ),
    },
    {
      title: "Table",
      dataIndex: "table_label",
      key: "table_label",
      width: 118,
      render: (value) => (
        <span
          className={`restaurant-orders__table-chip${
            value ? "" : " restaurant-orders__table-chip--empty"
          }`}
        >
          {value || "Sans table"}
        </span>
      ),
    },
    {
      title: "Service",
      dataIndex: "service_mode",
      key: "service_mode",
      width: 125,
      render: (value) => (
        <span className="restaurant-orders__service-value">
          {getServiceModeLabel(value)}
        </span>
      ),
    },
    {
      title: "Couverts",
      dataIndex: "guest_count",
      key: "guest_count",
      width: 95,
      align: "center",
      render: (value) => (
        <span className="restaurant-orders__count-badge">
          {Number(value) || 0}
        </span>
      ),
    },
    {
      title: "Serveur",
      dataIndex: "waiter_name",
      key: "waiter_name",
      width: 145,
      render: (value) => (
        <span className={value ? "" : "restaurant-orders__muted-value"}>
          {value || "Non renseigné"}
        </span>
      ),
    },
    ...statusColumns,
    {
      title: "Montant",
      key: "total",
      width: 140,
      align: "right",
      render: (_, record) => (
        <div className="restaurant-orders__cell-stack restaurant-orders__cell-stack--align-end">
          <Text strong className="restaurant-orders__money-value">
            {formatMoney(record.total)} CFA
          </Text>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 76,
      align: "center",
      render: (_, record) => renderOrderActions(record),
    },
  ];

  if (!isRestaurantActivity) {
    return (
      <Card>
        <Title level={3}>Prise de commande restaurant</Title>
        <Alert
          type="warning"
          showIcon
          message="Module reserve a la restauration"
          description="La prise de commande est visible uniquement pour les entreprises dont le profil d'activite est Restauration."
        />
      </Card>
    );
  }

  if (!canView) {
    return (
      <Card>
        <Title level={3}>Prise de commande restaurant</Title>
        <Alert
          type="error"
          showIcon
          message="Acces refuse"
          description="Vous n'avez pas la permission de consulter les commandes salle."
        />
      </Card>
    );
  }

  if (!selectedWarehouse) {
    return (
      <Card>
        <Title level={3}>Prise de commande restaurant</Title>
        <Empty
          description="Selectionnez un établissement pour prendre des commandes restaurant."
        />
      </Card>
    );
  }

  return (
    <>
      <div className="gestion-produit-container restaurant-orders restaurant-orders__list-page p-sm md:p-md">
        <div
          className="header-section restaurant-orders__list-toolbar flex flex-col md:flex-row md:justify-between md:items-center gap-md p-md shadow rounded-lg"
          style={{ backgroundColor: "var(--bg-primary)" }}
        >
          <div className="button-group flex flex-wrap items-center gap-sm">
            <Space size="middle">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateDrawer}
                disabled={!canCreate}
              >
                Nouvelle commande
              </Button>
              <BusinessAudioToggle
                alertType="production_confirmed"
                label="Sons production"
              />
            </Space>
          </div>

          <div className="filter-section flex flex-wrap items-center gap-sm md:gap-md mt-sm md:mt-0">
            <Input
              placeholder="Table, serveur, n° de commande..."
              allowClear
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              className="w-full sm:w-auto md:max-w-xs lg:max-w-sm"
              prefix={<SearchOutlined />}
            />
            <Select
              value={flowStatusFilter}
              onChange={setFlowStatusFilter}
              className="w-full sm:w-auto md:min-w-[180px] lg:min-w-[200px]"
              options={FLOW_STATUS_FILTER_OPTIONS}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadOrders()}
              loading={loading}
            >
              Rafraîchir
            </Button>
          </div>
        </div>

        {isMobileLayout ? (
          loading && orders.length === 0 ? (
            <Card loading size="small" className="restaurant-orders__mobile-card" />
          ) : orders.length === 0 ? (
            <Empty description="Aucune commande restaurant ne correspond au filtre courant." />
          ) : (
            <div className="restaurant-orders__mobile-list">
              {orders.map((record) => {
                const preview = getOrderItemPreview(record, 3);

                return (
                  <article
                    key={record.id}
                    className="restaurant-orders__mobile-card"
                  >
                    <div className="restaurant-orders__mobile-card-head">
                      <div>
                        <div className="restaurant-orders__mobile-order-number">
                          {record.order_number || `#${record.id}`}
                        </div>
                        <div className="restaurant-orders__mobile-date">
                          <CalendarOutlined />
                          <span>{formatDateTime(record.order_date)}</span>
                        </div>
                      </div>
                      <div className="restaurant-orders__mobile-total-pill">
                        <DollarOutlined />
                        <span>{formatMoney(record.total)} CFA</span>
                      </div>
                    </div>

                    {renderOrderStatusOverview(record)}

                    <div className="restaurant-orders__mobile-metrics">
                      <div className="restaurant-orders__mobile-metric">
                        <span className="restaurant-orders__mobile-metric-label">
                          Table
                        </span>
                        <strong>{record.table_label || "Sans table"}</strong>
                      </div>
                      <div className="restaurant-orders__mobile-metric">
                        <span className="restaurant-orders__mobile-metric-label">
                          Service
                        </span>
                        <strong>{getServiceModeLabel(record.service_mode)}</strong>
                      </div>
                      <div className="restaurant-orders__mobile-metric">
                        <span className="restaurant-orders__mobile-metric-label">
                          Couverts
                        </span>
                        <strong>{Number(record.guest_count) || 0}</strong>
                      </div>
                      <div className="restaurant-orders__mobile-metric">
                        <span className="restaurant-orders__mobile-metric-label">
                          Serveur
                        </span>
                        <strong>{record.waiter_name || "Non renseigne"}</strong>
                      </div>
                    </div>

                    <div className="restaurant-orders__mobile-content">
                      <div className="restaurant-orders__mobile-content-head">
                        <span>{formatOrderLineCount(record.total_items)}</span>
                        <span>{formatQuantity(record.total_quantity)}</span>
                      </div>
                      {preview.items.length > 0 ? (
                        <div className="restaurant-orders__preview-list">
                          {preview.items.map((itemName) => (
                            <span
                              key={`${record.id}-mobile-${itemName}`}
                              className="restaurant-orders__preview-pill"
                            >
                              {itemName}
                            </span>
                          ))}
                          {preview.remaining > 0 && (
                            <span className="restaurant-orders__preview-more">
                              +{preview.remaining} autre(s)
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="restaurant-orders__mobile-content-empty">
                          Aucun detail article charge sur cette ligne.
                        </span>
                      )}
                    </div>

                    {record.notes_front ? (
                      <div className="restaurant-orders__mobile-note">
                        Salle : {record.notes_front}
                      </div>
                    ) : null}

                    {renderOrderActions(record)}
                  </article>
                );
              })}
            </div>
          )
        ) : (
          <div
            className="overflow-x-auto shadow rounded-lg restaurant-orders__table-card"
            style={{ backgroundColor: "var(--bg-primary)" }}
          >
            <Table
              className="restaurant-orders__table"
              rowKey="id"
              loading={loading}
              dataSource={orders}
              columns={columns}
              expandable={desktopExpandable}
              scroll={{ x: showSeparateOperationalColumns ? 1380 : 1260 }}
              pagination={{
                ...ordersPagination,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                onChange: (current, pageSize) =>
                  setOrdersPagination((previous) => ({
                    current: pageSize === previous.pageSize ? current : 1,
                    pageSize,
                  })),
              }}
              locale={{
                emptyText: (
                  <Empty description="Aucune commande restaurant ne correspond au filtre courant." />
                ),
              }}
            />
          </div>
        )}
      </div>

      {isMobileLayout ? (
        <Drawer
          open={Boolean(detailOrder)}
          onClose={closeDetailModal}
          title={detailOrderTitle}
          placement="bottom"
          height="92vh"
          className="restaurant-orders__details-drawer"
          footer={
            detailOrder ? (
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={() => openPrintTicket(detailOrder)}
                loading={printingOrderId === Number(detailOrder.id)}
                block
              >
                Imprimer le ticket
              </Button>
            ) : null
          }
        >
          {renderDetailOrderContent()}
        </Drawer>
      ) : (
        <Modal
          open={Boolean(detailOrder)}
          onCancel={closeDetailModal}
          footer={
            detailOrder
              ? [
                  <Button key="close" onClick={closeDetailModal}>
                    Fermer
                  </Button>,
                  <Button
                    key="print"
                    type="primary"
                    icon={<PrinterOutlined />}
                    onClick={() => openPrintTicket(detailOrder)}
                    loading={printingOrderId === Number(detailOrder.id)}
                  >
                    Imprimer le ticket
                  </Button>,
                ]
              : null
          }
          width={920}
          title={detailOrderTitle}
        >
          {renderDetailOrderContent()}
        </Modal>
      )}

      <RestaurantOrderTicket
        visible={Boolean(ticketOrder)}
        order={ticketOrder}
        onClose={closePrintTicket}
      />

      <Modal
        open={Boolean(payOrder)}
        title={
          payOrder
            ? `Encaisser la commande ${payOrder.order_number || `#${payOrder.id}`}`
            : "Encaisser la commande"
        }
        onCancel={closePaymentModal}
        confirmLoading={paySubmitting}
        okText="Encaisser et generer le ticket"
        cancelText="Annuler"
        onOk={handleConfirmPayment}
        maskClosable={!paySubmitting}
        destroyOnClose
      >
        {payOrder && (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Total a payer">
                <Text strong>{formatMoney(payOrder.total)} CFA</Text>
              </Descriptions.Item>
              {payOrder.table_label && (
                <Descriptions.Item label="Table">
                  {payOrder.table_label}
                </Descriptions.Item>
              )}
              {(payOrder.client_name || payOrder.customer_name) && (
                <Descriptions.Item label="Client">
                  {payOrder.client_name || payOrder.customer_name}
                </Descriptions.Item>
              )}
            </Descriptions>

            <div>
              <Text>Mode de paiement</Text>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                placeholder="Selectionner un mode de paiement"
                value={payModeId || undefined}
                onChange={(value) => setPayModeId(value)}
                loading={paymentModesLoading}
                options={paymentModes.map((mode) => ({
                  value: mode.id,
                  label: mode.name,
                }))}
              />
            </div>

            <div>
              <Text>Montant encaisse</Text>
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                min={0}
                value={payAmount}
                onChange={(value) => setPayAmount(value ?? 0)}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                }
                parser={(value) => (value || "").replace(/\s/g, "")}
                addonAfter="CFA"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Laisser le montant total pour un paiement complet. Un montant
                inferieur enregistre un encaissement partiel.
              </Text>
            </div>
          </Space>
        )}
      </Modal>

      <TicketCaisse
        visible={Boolean(cashTicket)}
        order={cashTicket}
        onClose={closeCashTicket}
      />

      <Drawer
        width={drawerWidth}
        destroyOnClose
        open={drawerOpen}
        onClose={closeDrawer}
        title={
          drawerMode === "edit"
            ? `Modifier la commande ${draft.order_number || ""}`.trim()
            : "Nouvelle commande salle"
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              flexDirection: isMobileLayout ? "column" : "row",
            }}
          >
            <Button
              onClick={closeDrawer}
              disabled={saving}
              style={isMobileLayout ? { width: "100%" } : undefined}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveDraft}
              loading={saving}
              disabled={!isEditable}
              style={isMobileLayout ? { width: "100%" } : undefined}
            >
              Enregistrer
            </Button>
            <Button
              type="primary"
              icon={<FireOutlined />}
              onClick={handleSaveAndSend}
              loading={saving}
              disabled={!isEditable || !canSend}
              style={isMobileLayout ? { width: "100%" } : undefined}
            >
              Enregistrer et envoyer
            </Button>
          </div>
        }
      >
        {!isEditable && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message="Commande en lecture seule"
            description="Seules les commandes en brouillon ou confirmees peuvent etre modifiees."
          />
        )}
        <Form layout="vertical" disabled={!isEditable}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Mode de service">
                <Select
                  value={draft.service_mode}
                  onChange={(value) => updateDraftField("service_mode", value)}
                  options={SERVICE_MODE_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Table / emplacement">
                <Input
                  value={draft.table_label}
                  onChange={(event) =>
                    updateDraftField("table_label", event.target.value)
                  }
                  placeholder="ex: Table 12"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Nombre de couverts">
                <InputNumber
                  min={1}
                  value={draft.guest_count}
                  onChange={(value) =>
                    updateDraftField("guest_count", value || 1)
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Note pour la salle">
                <TextArea
                  rows={2}
                  value={draft.notes_front}
                  onChange={(event) =>
                    updateDraftField("notes_front", event.target.value)
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Note pour la cuisine">
                <TextArea
                  rows={2}
                  value={draft.notes_kitchen}
                  onChange={(event) =>
                    updateDraftField("notes_kitchen", event.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Card
              size="small"
              title="Catalogue"
              extra={
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => loadCatalog(catalogSearch)}
                  loading={catalogLoading}
                >
                  Actualiser
                </Button>
              }
            >
              <Input.Search
                allowClear
                placeholder="Rechercher un article..."
                value={catalogSearch}
                onChange={(event) => setCatalogSearch(event.target.value)}
                onSearch={(value) => loadCatalog(value)}
                style={{ marginBottom: 12 }}
              />
              <div style={{ maxHeight: 480, overflowY: "auto" }}>
                {filteredCatalog.length === 0 ? (
                  <Empty description="Aucun produit disponible" />
                ) : (
                  filteredCatalog.map((product) => (
                    <div
                      key={product.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 4px",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Text strong>{product.name}</Text>
                        </div>
                        <Text type="secondary">
                          {formatMoney(product.sales_price)}
                        </Text>
                      </div>
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => addProductToDraft(product)}
                        disabled={!isEditable}
                      />
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>

          <Col xs={24} md={14}>
            <Card size="small" title="Articles de la commande">
              {draft.items.length === 0 ? (
                <Empty description="Aucun article ajoute" />
              ) : isMobileLayout ? (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {draft.items.map((record, index) => (
                    <Card key={`${record.product_id}-${index}`} size="small">
                      <Space direction="vertical" size={10} style={{ width: "100%" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            alignItems: "flex-start",
                          }}
                        >
                          <div>
                            <Text strong>{record.product_name}</Text>
                            {record.unit_short_name && (
                              <div>
                                <Text type="secondary">{record.unit_short_name}</Text>
                              </div>
                            )}
                          </div>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeDraftItem(index)}
                            disabled={!isEditable}
                          />
                        </div>
                        <div>
                          <Text type="secondary">Quantite</Text>
                          <InputNumber
                            min={0.01}
                            step={1}
                            value={record.quantity}
                            onChange={(value) =>
                              updateDraftItem(index, "quantity", value || 0)
                            }
                            disabled={!isEditable}
                            style={{ width: "100%", marginTop: 4 }}
                          />
                        </div>
                        <div>
                          <Text type="secondary">Prix unitaire</Text>
                          <InputNumber
                            min={0}
                            step={100}
                            value={record.unit_price}
                            onChange={(value) =>
                              updateDraftItem(index, "unit_price", value || 0)
                            }
                            disabled={!isEditable}
                            style={{ width: "100%", marginTop: 4 }}
                          />
                        </div>
                        <div>
                          <Text strong>
                            Sous-total:{" "}
                            {formatMoney(
                              (Number(record.quantity) || 0) *
                                (Number(record.unit_price) || 0)
                            )}
                          </Text>
                        </div>
                      </Space>
                    </Card>
                  ))}
                </Space>
              ) : (
                <Table
                  size="small"
                  rowKey={(record) => `${record.product_id}-${record.course_label || ""}`}
                  dataSource={draft.items}
                  pagination={false}
                  scroll={{ x: 520 }}
                  columns={draftItemColumns}
                />
              )}
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobileLayout ? "flex-start" : "center",
                  flexDirection: isMobileLayout ? "column" : "row",
                  gap: 8,
                }}
              >
                <Text type="secondary">
                  {draftTotals.total_items} article(s) -{" "}
                  {formatQuantity(draftTotals.total_quantity)}
                </Text>
                <Title level={4} style={{ margin: 0 }}>
                  Total: {formatMoney(draftTotals.total)}
                </Title>
              </div>
            </Card>
          </Col>
        </Row>
      </Drawer>
    </>
  );
};

export default RestaurantOrders;
